import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';

import style from './SupportWidget.module.scss';

import type { SupportTicketStatusOption } from 'chaika-frontend-types/frontend-cms';

import { translate } from '@chaika/i18n/helper';

import { describeStatusFilter, SupportStatusFilter, toggleStatusName } from './utils/ticketListFilter';

interface Props {
    filter: SupportStatusFilter;
    statuses?: SupportTicketStatusOption[];
    onChange(filter: SupportStatusFilter): void;
    onReset(): void;
}

export function TicketStatusFilter({ filter, statuses, onChange, onReset }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const label = describeStatusFilter(filter);
    const selectedNames = filter.kind === 'names' ? filter.names : [];
    const statusNames = statuses?.map(status => status.name) ?? [];

    return (
        <Popover.Root
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <Popover.Trigger asChild>
                <button
                    className={style.statusFilterButton}
                    type={'button'}
                    aria-label={`${translate('supportFilterLabel')}: ${label}`}
                    title={translate('supportFilterLabel')}
                >
                    <span className={style.statusFilterLabel}>{label}</span>
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className={style.popover}
                    side={'bottom'}
                    align={'end'}
                    sideOffset={4}
                    aria-label={translate('supportFilterLabel')}
                >
                    <button
                        className={classNames(style.statusFilterOption, filter.kind === 'open' && style.selectedOption)}
                        type={'button'}
                        aria-pressed={filter.kind === 'open'}
                        onClick={() => onChange({ kind: 'open' })}
                    >
                        {translate('supportFilterOpenOnly')}
                    </button>
                    <button
                        className={classNames(style.statusFilterOption, filter.kind === 'all' && style.selectedOption)}
                        type={'button'}
                        aria-pressed={filter.kind === 'all'}
                        onClick={() => onChange({ kind: 'all' })}
                    >
                        {translate('supportFilterAllStatuses')}
                    </button>
                    {statuses && statuses.length > 0 && (
                        <>
                            <hr className={style.popoverDivider} />
                            {statuses.map(status => (
                                <label
                                    key={status.name}
                                    className={style.statusFilterOption}
                                >
                                    <input
                                        type={'checkbox'}
                                        name={status.name}
                                        checked={selectedNames.includes(status.name)}
                                        onChange={() => onChange(toggleStatusName(filter, status.name, statusNames))}
                                    />
                                    <span>{status.name}</span>
                                </label>
                            ))}
                        </>
                    )}
                    <hr className={style.popoverDivider} />
                    <button
                        className={style.statusFilterReset}
                        type={'button'}
                        onClick={onReset}
                    >
                        {translate('supportFilterReset')}
                    </button>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
